/**
 * Every error string the portal can render, keyed.
 *
 * Four rules, all asserted in src/test/copy-honesty.test.ts:
 *
 *  1. Never echo a server `message`. No template here contains a {message} placeholder,
 *     and mapError never writes one.
 *  2. On a money surface, SAY WHAT DID NOT HAPPEN. "Nothing was charged." "Your
 *     subscription is unchanged." That clause is the difference between an error and a
 *     panic when the screen is about someone's card.
 *  3. Every error has an action, or says what happens next. Never a dead end.
 *  4. Never echo an identifier the user did not type. `{email} is already on your team.`
 *     echoes an address they typed in this session — fine. A server-supplied id is not.
 */

export interface ErrorCopy {
  /** Short, sentence case, no exclamation marks. */
  title?: string;
  body: string;
  /** Label of the primary action, or null when the copy states what happens next. */
  action: string | null;
  /**
   * Rendered inline, beside the field or control that produced it, on a form that is still
   * on screen and still filled in.
   *
   * Such a message needs no action label: the form IS the affordance, and adding a "Try
   * again" button next to a live submit button would be noise. This is a flag rather than a
   * prose heuristic so the "never a dead end" rule stays reviewable — every entry either
   * offers an action, says what happens next, or is explicitly marked inline.
   */
  inline?: boolean;
}

/** Interpolation is limited to values the user typed themselves. */
export type CopyVars = { email?: string; seats?: number; service?: string; date?: string };

const TABLE: Record<string, ErrorCopy> = {
  // ---- generic ----------------------------------------------------------------
  "error.validation": {
    body: "Something in that request wasn't right. Check your entries and try again.",
    action: null,
    inline: true,
  },
  "error.notFound": { body: "We couldn't find that.", action: "Go to Home" },
  "error.conflict": { body: "That didn't go through because something changed. Refresh and try again.", action: "Refresh" },
  "error.cooldown": { body: "Too many tries. Try again in a moment.", action: null, inline: true },
  "error.unavailable": { body: "That service is unavailable right now. Nothing changed — try again in a moment.", action: "Retry" },
  "error.unknown": { body: "Something went wrong on our end. Nothing was changed.", action: "Retry" },
  "error.network": { body: "We couldn't reach the server. Nothing was saved — try again.", action: "Retry" },
  "error.offline": {
    body: "You're offline. Showing your last-loaded information. We'll reconnect automatically.",
    action: null,
    inline: true,
  },
  "error.sessionExpired": { body: "Your session expired. Sign in to continue.", action: "Sign in" },

  // ---- money-surface variants of the generic messages ---------------------------
  // Reachable only from a billing, checkout, invite or delete context. The difference is
  // one clause, and on a screen about someone's card it is the difference between an error
  // and a panic.
  "error.money.validation": {
    body: "Something in that request wasn't right. Nothing was charged — check your entries and try again.",
    action: null,
    inline: true,
  },
  "error.money.notFound": {
    body: "We couldn't find that. Nothing was charged and your subscription is unchanged.",
    action: "Go to Home",
  },
  "error.money.conflict": {
    body: "That didn't go through because something changed. Nothing was charged — refresh and try again.",
    action: "Refresh",
  },
  "error.money.cooldown": {
    body: "Too many tries. Nothing was charged — try again in a moment.",
    action: null,
    inline: true,
  },

  // ---- scope ------------------------------------------------------------------
  "error.adminOnly": { body: "Only admins can do that. Ask your team admin.", action: null, inline: true },
  "error.adminOnly.billing": { body: "Billing is managed by your team admin.", action: null, inline: true },
  "error.adminOnly.policy": { body: "Shared policy is managed by your team admin.", action: null, inline: true },
  "error.adminOnly.keys": { body: "API keys are managed by your team admin.", action: null, inline: true },
  "error.planRequired": { body: "That's available on Business.", action: "See plans" },

  // ---- auth -------------------------------------------------------------------
  // One message for wrong-password and unknown-user. Distinguishing them is enumeration.
  "error.auth.badCredentials": { body: "That email and password don't match.", action: null, inline: true },
  "error.auth.emailInUse": { body: "An account already exists for this email. Log in instead.", action: "Log in" },
  "error.auth.weakPassword": { body: "Use at least 8 characters, including a number.", action: null, inline: true },
  "error.auth.popupClosed": { body: "The Google sign-in window closed before it finished. Nothing was saved — try again.", action: "Try again" },
  "error.auth.unavailable": {
    body: "We couldn't create your account. Nothing was charged and nothing was saved — try again.",
    action: "Try again",
  },
  "error.auth.linkExpired": { body: "That link has expired or has already been used.", action: "Send a new link" },
  "error.auth.notVerifiedYet": {
    body: "We haven't seen that yet. Click the link in the email, then try again.",
    action: null,
    inline: true,
  },

  // ---- account ----------------------------------------------------------------
  "error.account.save": { body: "We couldn't save that. Your details are unchanged — try again.", action: "Retry" },
  "error.account.soleAdmin": {
    body: "You're the only admin of a team with other members. Nothing was deleted — promote another admin first.",
    action: "Go to Team",
  },
  "error.account.deleteUnavailable": {
    body: "Deleting your account isn't available yet. Nothing was deleted. Contact support and we'll do it for you.",
    action: "Contact support",
  },

  // ---- team -------------------------------------------------------------------
  "error.team.seatLimit": {
    body: "All {seats} seats are in use. Nothing was charged — add seats to invite more.",
    action: "Add seats",
  },
  "error.team.alreadyMember": { body: "{email} is already on your team. Nothing was sent.", action: null, inline: true },
  "error.team.lastAdmin": { body: "You're the only admin. Promote someone else first.", action: "Close" },
  "error.team.load": { body: "We couldn't load your team.", action: "Retry" },

  // ---- policy -----------------------------------------------------------------
  "error.policy.invalid": { body: "Default model must be one of the allowed models.", action: null, inline: true },
  "error.policy.versionConflict": {
    body: "Someone else changed this policy. Your changes haven't been saved yet.",
    action: "Review",
  },
  "error.policy.unavailable": {
    body: "Your policy wasn't saved. Nothing changed — your team is still on the previous settings. Try again in a moment.",
    action: "Retry",
  },
  "error.policy.load": { body: "We couldn't load your shared policy.", action: "Retry" },

  // ---- keys -------------------------------------------------------------------
  "error.keys.scopeExceeded": {
    body: "That product isn't on your plan any more. No key was created — refresh and try again.",
    action: "Refresh",
  },
  "error.keys.load": { body: "We couldn't load your API keys.", action: "Retry" },

  // ---- billing ----------------------------------------------------------------
  "error.billing.unavailable": {
    body: "Stripe is unavailable right now. Your subscription is unchanged — try again in a moment.",
    action: "Retry",
  },
  "error.billing.load": {
    body: "We couldn't load your billing details. Your subscription is unchanged.",
    action: "Retry",
  },
  "error.billing.invoices": { body: "We couldn't load your invoices. Nothing was charged.", action: "Retry" },
  "error.billing.popupBlocked": {
    body: "Your browser blocked the new tab. Your subscription is unchanged.",
    action: "Open Stripe billing",
  },

  // ---- checkout ---------------------------------------------------------------
  "error.checkout.invalid": {
    body: "We couldn't start checkout with those options. Nothing was charged — check your selection and try again.",
    action: "Try again",
  },
  "error.checkout.alreadySubscribed": {
    body: "You already have an active subscription. Nothing was charged.",
    action: "Manage billing",
  },
  "error.checkout.inProgress": {
    body: "You already have a checkout in progress. Nothing was charged twice.",
    action: "Refresh",
  },

  // ---- degraded, not errors ----------------------------------------------------
  // These are reachable from mapError but are STATES, not failures. They live here so the
  // resolver is total and so nobody has to remember which is which at the call site.
  "usage.unavailable": { body: "Usage data unavailable", action: null, inline: true },
  "state.trial": { body: "Your trial is running.", action: null, inline: true },
};

/**
 * Resolve a copy key. Unknown keys throw in development and fall back to the generic
 * message in production — a missing string should be a build failure, never a blank screen
 * on a billing page.
 */
export function errorCopy(key: string, vars: CopyVars = {}): ErrorCopy {
  const entry = TABLE[key];
  if (!entry) {
    if (import.meta.env?.DEV) throw new Error(`Unknown error copy key: ${key}`);
    return TABLE["error.unknown"]!;
  }
  return {
    ...(entry.title ? { title: interpolate(entry.title, vars) } : {}),
    body: interpolate(entry.body, vars),
    action: entry.action,
  };
}

function interpolate(s: string, vars: CopyVars): string {
  return s.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const v = (vars as Record<string, unknown>)[name];
    return v === undefined ? whole : String(v);
  });
}

/** Exported so the honesty test can enumerate the table rather than sample it. */
export const ERROR_COPY_TABLE: Readonly<Record<string, ErrorCopy>> = TABLE;
